const toCleanString = (value) => String(value || '').trim();

const trimTrailingSlashes = (value) => toCleanString(value).replace(/\/+$/, '');

const isAbsoluteUrl = (value) => /^https?:\/\//i.test(toCleanString(value));

const getViteEnv = () => {
  try {
    if (typeof import.meta !== 'undefined' && import.meta.env) {
      return import.meta.env;
    }
  } catch {
    // ignore
  }
  return {};
};

const getMergedEnv = () => {
  const viteEnv = getViteEnv();
  const craEnv =
    typeof process !== 'undefined' && process.env
      ? process.env
      : {};

  return {
    ...craEnv,
    ...viteEnv,
  };
};

const getFirstEnv = (...keys) => {
  const env = getMergedEnv();
  for (const key of keys) {
    const value = toCleanString(env[key]);
    if (value) return value;
  }
  return '';
};

const getBrowserApiFallback = () => {
  // Build / SSR fallback
  if (typeof window === 'undefined') return 'http://127.0.0.1:8000/api';

  const { protocol, hostname } = window.location;
  const isLocalHost =
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    hostname === '::1';

  // Keep local dev behavior unchanged.
  if (isLocalHost) return 'http://127.0.0.1:8000/api';

  // Production-safe fallback when env is missing.
  // (App is mounted at the domain root; Laravel public is the DocumentRoot.)
  return `${protocol}//${hostname}/api`;
};

const normalizeApiBase = (rawValue) => {
  const value = trimTrailingSlashes(rawValue);
  if (!value) return getBrowserApiFallback();
  return /\/api$/i.test(value) ? value : `${value}/api`;
};

const normalizeBackendBase = (rawValue) => {
  let value = trimTrailingSlashes(rawValue);
  if (!value) return '';

  value = value
    .replace(/\/api$/i, '')
    .replace(/\/storage\/app\/public$/i, '')
    .replace(/\/storage\/app\/private$/i, '')
    .replace(/\/storage$/i, '');

  return trimTrailingSlashes(value);
};

const sanitizePath = (path) =>
  toCleanString(path)
    .replace(/\\/g, '/')
    .replace(/^['"]|['"]$/g, '')
    .replace(/^\/+/, '');

const joinUrl = (base, path) => {
  const cleanBase = trimTrailingSlashes(base);
  const cleanPath = sanitizePath(path);
  if (!cleanBase || !cleanPath) return null;
  return `${cleanBase}/${cleanPath}`;
};

const normalizePublicStoragePath = (path) => {
  let value = sanitizePath(path);
  if (!value) return '';

  value = value
    .replace(/^storage\/app\/public\//i, '')
    .replace(/^app\/public\//i, '')
    .replace(/^public\//i, '');

  if (/^storage\//i.test(value)) return value;
  return `storage/${value}`;
};

const normalizePrivateStoragePath = (path) => {
  let value = sanitizePath(path);
  if (!value) return '';

  value = value
    .replace(/^storage\/app\/private\//i, '')
    .replace(/^app\/private\//i, '')
    .replace(/^private\//i, '');

  if (/^storage\//i.test(value)) return value;
  return `storage/app/private/${value}`;
};

// IMPORTANT: use LITERAL process.env.X accesses below so CRA's webpack
// DefinePlugin can statically inline the values at build time. Dynamic
// access like `env[key]` does NOT get replaced in CRA builds, which causes
// the production fallback in getBrowserApiFallback() to be used at runtime.
export const getApiBase = () => {
  const value =
    toCleanString(process.env.REACT_APP_API_URL) ||
    toCleanString(process.env.REACT_APP_API_BASE_URL) ||
    '';
  return normalizeApiBase(value);
};

export const getBackendBase = () => {
  const explicitBase = normalizeBackendBase(
    toCleanString(process.env.REACT_APP_BACKEND_URL) ||
    toCleanString(process.env.REACT_APP_APP_BACKEND_URL) ||
    ''
  );
  if (explicitBase) return explicitBase;
  return normalizeBackendBase(getApiBase());
};

export const buildBackendUrl = (path) => {
  const value = toCleanString(path);
  if (!value) return null;
  if (isAbsoluteUrl(value)) return value;
  return joinUrl(getBackendBase(), value);
};

export const buildPublicFileUrl = (path) => {
  const value = toCleanString(path);
  if (!value) return null;
  if (isAbsoluteUrl(value)) return value;
  return joinUrl(getBackendBase(), normalizePublicStoragePath(value));
};

export const buildAttachmentUrl = (path) => {
  const value = toCleanString(path);
  if (!value) return null;
  if (isAbsoluteUrl(value)) return value;

  const normalized = sanitizePath(value);
  if (/^(public\/|app\/public\/|storage\/app\/public\/|storage\/)/i.test(normalized)) {
    return buildPublicFileUrl(normalized);
  }

  const explicitAttachmentBase = trimTrailingSlashes(
    getFirstEnv('VITE_ATTACHMENT_URL', 'REACT_APP_ATTACHMENT_URL')
  );

  if (explicitAttachmentBase) {
    return joinUrl(explicitAttachmentBase, normalized);
  }

  return joinUrl(getBackendBase(), normalizePrivateStoragePath(normalized));
};
